import type { KeyboardEvent } from 'react';
import styled from 'styled-components';

import { Card, CardContent } from '@/components/ui/card';

import { formatVolume, type PlanningAggregate } from '../constants/dynamicDeliveryColumns';

export type TourSummary = {
    approCount: number;
    stopCount: number;
    overloadedCount: number;
};

type SummaryCardsProps = {
    aggregate: PlanningAggregate;
    tours: TourSummary;
    // Urgences card: toggles the table's motif filter on the urgent presets; active = preset currently applied.
    urgentFilterActive: boolean;
    onToggleUrgentFilter: () => void;
    // Tournées card: jumps to the map view, where the tours live.
    onShowMap: () => void;
};

// Button semantics on a Card div (a real <button> would drop the Card's base classes): click + Enter/Space.
const pressableProps = (onActivate: () => void) => ({
    role: 'button' as const,
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onActivate();
        }
    },
});

export const SummaryCards = ({
    aggregate,
    tours,
    urgentFilterActive,
    onToggleUrgentFilter,
    onShowMap,
}: SummaryCardsProps) => (
    <CardsRow>
        <SummaryCard>
            <SummaryContent>
                <SummaryLabel>Colonnes à livrer</SummaryLabel>
                <SummaryValue>{aggregate.deliverCount}</SummaryValue>
                <SubLine>
                    {aggregate.deliver.kg.toFixed(1)} kg · {formatVolume(aggregate.deliver.volume)} cm³
                </SubLine>
            </SummaryContent>
        </SummaryCard>
        <ClickableCard
            {...pressableProps(onToggleUrgentFilter)}
            $active={urgentFilterActive}
            title="Filtrer la liste sur les motifs Trop ancien · Critique"
            data-test="planning-card-urgences"
        >
            <SummaryContent>
                <SummaryLabel>Urgences à livrer</SummaryLabel>
                <SummaryValue $alert={aggregate.urgent.deliverTotal > 0}>{aggregate.urgent.deliverTotal}</SummaryValue>
                <SubLine>
                    {aggregate.urgent.deliverCritical} critique{aggregate.urgent.deliverCritical > 1 ? 's' : ''} ·{' '}
                    {aggregate.urgent.deliverTooOld} trop ancienne{aggregate.urgent.deliverTooOld > 1 ? 's' : ''}
                </SubLine>
            </SummaryContent>
        </ClickableCard>
        <ClickableCard
            {...pressableProps(onShowMap)}
            $active={false}
            title="Voir les tournées sur la carte"
            data-test="planning-card-tournees"
        >
            <SummaryContent>
                <SummaryLabel>Tournées</SummaryLabel>
                <SummaryValue>
                    {tours.approCount}
                    <Unit>
                        appro{tours.approCount > 1 ? 's' : ''} · {tours.stopCount} arrêt
                        {tours.stopCount > 1 ? 's' : ''}
                    </Unit>
                </SummaryValue>
                <SubLine>
                    {tours.overloadedCount > 0 ? (
                        <AlertText>
                            ⚠ {tours.overloadedCount} tournée{tours.overloadedCount > 1 ? 's' : ''} &gt; 7h
                        </AlertText>
                    ) : (
                        ' '
                    )}
                </SubLine>
            </SummaryContent>
        </ClickableCard>
        <SummaryCard>
            <SummaryContent>
                <SummaryLabel>Colonnes potentielles</SummaryLabel>
                <SummaryValue>{aggregate.potentialCount}</SummaryValue>
                <SubLine>
                    dont {aggregate.urgent.potentialTotal} critique{aggregate.urgent.potentialTotal > 1 ? 's' : ''}
                </SubLine>
            </SummaryContent>
        </SummaryCard>
    </CardsRow>
);

const CardsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const SummaryCard = styled(Card)`
    padding: 0;
    text-align: left;
`;

// Card that doubles as a button (filter toggle / view jump) — strictly the same base as the plain cards;
// hover/active swap the Card's ring for the CTA colour (box-shadow: no layout shift). `&&` outranks the
// Tailwind ring utility whatever the stylesheet injection order.
const ClickableCard = styled(SummaryCard)<{ $active: boolean }>`
    cursor: pointer;
    user-select: none;
    transition: box-shadow 0.15s ease;

    ${({ theme, $active }) => ($active ? `&& { box-shadow: 0 0 0 1px ${theme.ctaPrimaryColor}; }` : '')}

    &&:hover {
        box-shadow: 0 0 0 1px ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const SummaryContent = styled(CardContent)`
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SummaryLabel = styled.span`
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const SummaryValue = styled.span<{ $alert?: boolean }>`
    font-size: 22px;
    font-weight: 700;
    color: ${({ theme, $alert }) => ($alert ? theme.errorColor : theme.textColor)};
    display: flex;
    align-items: baseline;
    gap: 6px;
`;

const Unit = styled.span`
    font-size: 13px;
    font-weight: 400;
    color: ${({ theme }) => theme.infoTextColor};
`;

const SubLine = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const AlertText = styled.span`
    color: ${({ theme }) => theme.errorColor};
    font-weight: 600;
`;
